import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { bufferTime, catchError, debounceTime, filter, mergeMap, switchMap } from 'rxjs/operators';

import { CommonEpic } from 'common/types/redux';

import assetsActionTypes from './assets.actionTypes';
import * as actions from './assets.actions';
import * as assetsApi from './assets.api';

export const epicGetAssets: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_ASSETS),
    mergeMap(() =>
      from(api.getAssets()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetAssets(response))),
        catchError((error) => of(actions.actionErrorGetAssets(error))),
      ),
    ),
  );

export const epicGetCoins: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_COINS),
    mergeMap(() =>
      from(api.getCoins()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetCoins(response))),
        catchError((error) => of(actions.actionErrorGetAssets(error))),
      ),
    ),
  );

export const epicGetAssetsTrend: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_SPOT_TREND),
    bufferTime(2000),
    filter((stream) => !!stream.length),
    switchMap((actionsCollection) => {
      const symbols = actionsCollection.reduce((acc: string[], curr) => [...acc, ...curr.payload], []);
      return from(api.getSpotDailyCandles(symbols)).pipe(
        mergeMap((response) => of(actions.actionSuccessGetSpotTrend(response))),
        catchError((error) => of(actions.actionErrorGetSpotTrend(error))),
      );
    }),
  );

export const epicGetSpotCommissions: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_SPOT_COMMISSIONS),
    mergeMap(() =>
      from(api.getSpotCommissions()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetSpotCommissions(response))),
        catchError((error) => of(actions.actionErrorGetSpotCommissions(error))),
      ),
    ),
  );

export const epicGetAssetsGroups: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_ASSETS_GROUPS),
    mergeMap(() =>
      from(api.getAssetsGroups()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetAssetsGroups(response))),
        catchError((error) => of(actions.actionErrorGetAssetsGroups(error))),
      ),
    ),
  );

export const epicGetCoinsGroups: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_COINS_GROUPS),
    mergeMap(() =>
      from(api.fetchCoinCategories()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetCoinsGroups(response))),
        catchError((error) => of(actions.actionErrorGetCoinsGroups(error))),
      ),
    ),
  );

export const epicGetFavoriteAssets: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_FAVORITE_ASSETS),
    mergeMap(() =>
      from(api.getFavoriteAssets()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetFavoriteAssets(response))),
        catchError((error) => of(actions.actionErrorGetFavoriteAssets(error))),
      ),
    ),
  );

export const epicGetFavoriteCoins: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_FAVORITE_COINS),
    mergeMap(() =>
      from(api.getFavoriteCoins()).pipe(
        mergeMap((response) => of(actions.actionSuccessGetFavoriteCoins(response))),
        catchError((error) => of(actions.actionErrorGetFavoriteCoins(error))),
      ),
    ),
  );

export const epicSetFavoriteAsset: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_SET_FAVORITE_ASSET),
    mergeMap((action) => {
      const { favoritesAssets } = _state$.value.assetsReducer;
      const code = action.payload.replace('-', '/');
      const endpoint = () =>
        favoritesAssets.includes(code) ? api.deleteFavoriteAsset(code) : api.setFavoriteAsset(code);
      return from(endpoint()).pipe(
        mergeMap((response) => of(actions.actionSuccessSetFavoriteAssets(response), actions.actionGetFavoriteAssets())),
        catchError((error) => of(actions.actionErrorSetFavoriteAssets(error))),
      );
    }),
  );

export const epicSetFavoriteCoin: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_SET_FAVORITE_COIN),
    mergeMap((action) => {
      const { favoritesCoins } = _state$.value.assetsReducer;
      const endpoint = () =>
        favoritesCoins.includes(action.payload)
          ? api.deleteFavoriteCoin(action.payload)
          : api.setFavoriteCoin(action.payload);
      return from(endpoint()).pipe(
        mergeMap((response) => of(actions.actionSuccessSetFavoriteCoins(response), actions.actionGetFavoriteCoins())),
        catchError((error) => of(actions.actionErrorSetFavoriteCoins(error))),
      );
    }),
  );

export const epicGetAssetInfo: CommonEpic<typeof assetsApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(assetsActionTypes.LOADING_GET_ASSET_INFO),
    debounceTime(1000),
    mergeMap((action) =>
      from(api.getAssetInfo(action.payload)).pipe(
        mergeMap((response) => of(actions.actionSuccessGetAssetInfo(response))),
        catchError((error) => of(actions.actionErrorGetAssetInfo(error))),
      ),
    ),
  );
